import { ChevronDownIcon } from '@chakra-ui/icons'
import type {
  FlexProps
} from '@chakra-ui/react'
import {
  Box,
  Divider,
  Flex,
  Grid,
  HStack,
  Image,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useNavigateToUnauthorized } from '@/hooks/useCustomNavigate'

import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useApp } from '@/hooks/useApp'
import { useAvailableApps } from '../hooks/useProducts'

interface AppLinkProps extends FlexProps {
  name: string
  icon: string
  iconSize?: number
  isCurrent?: boolean
  hasCaret?: boolean
  onCaretClick?: () => void
  onImageClick?: () => void
}

const AppLink = ({
  name,
  icon,
  iconSize,
  isCurrent = false,
  hasCaret = false,
  onCaretClick,
  onImageClick,
  sx,
  ...rest
}: AppLinkProps) => {
  return (
    <Flex
      sx={{
        w: 'fit-content',
        alignItems: 'center',
        bgColor: 'white',
        borderRadius: isCurrent ? 'md' : 'none',
        cursor: 'pointer',
        fontWeight: isCurrent ? 'bold' : 'normal',
        _hover: {
          bgColor: 'gray.50',
        },
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
      onClick={(e) => {
        if (onImageClick) {
          e.preventDefault()
          e.stopPropagation()
          onImageClick()
        }
      }}
    >
      <Image
        ml={3}
        my={2}
        mr={4}
        height={'28px'}
        src={icon}
        alt={name}
        sx={{ w: iconSize, h: iconSize }}
      />

      <Text pr={3} lineHeight={1.2}>{name}</Text>

      {hasCaret && (
        <Box
          alignSelf="stretch"
          alignContent="center"
          px={3}
          onClick={(e) => {
            e.stopPropagation()
            if (onCaretClick) {
              e.preventDefault()
              onCaretClick()
            }
          }}
          _hover={{ bgColor: 'gray.100' }}
        >
          <ChevronDownIcon boxSize={4} />
        </Box>
      )}
    </Flex>
  )
}

export const AppSwitcher = () => {
  const boxRef = useRef<HTMLDivElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const apps = useAvailableApps()
  useOutsideClick({
    ref: boxRef,
    handler: () => onClose(),
  })
  const navigate = useNavigate()
  const navigateToUnauthorized = useNavigateToUnauthorized()
  const { isLoggingOut, isLoading, user } = useAuth()
  const { currentPartner } = usePartner()

  const { lastApp, setLastApp } = useApp()

  const shouldNavigate = useMemo(() => !!lastApp && apps.length > 0 && !apps.includes(lastApp), [lastApp, apps])
  const noAvailableApp = useMemo(() => apps.length === 0, [apps])
  const isLogged = !!user?.logged

  useEffect(() => {
    if (shouldNavigate) 
      navigate(apps[0].path!)
  }, [apps, navigate, shouldNavigate])

  useEffect(() => {
    // Only navigate to unauthorized if user is actually logged in but has no available apps
    if (noAvailableApp && !isLoggingOut && !isLoading && isLogged)
      navigateToUnauthorized('noProductsAvailable')
  }, [noAvailableApp, isLoggingOut, isLoading, isLogged, navigateToUnauthorized])

  useEffect(() => {
    if (!lastApp && apps.length > 0)
      setLastApp(apps[0].app)
  }, [apps, lastApp, setLastApp])

  if (shouldNavigate || noAvailableApp) {
    return null
  }
  
  // Check for special partners
  const isEssox = ['Essox', 'AutoEsa', 'DirectAuto'].includes(currentPartner?.partner ?? '')
  const isTUkas = currentPartner?.partner == 'TUkas'
  const isVWFS = currentPartner?.partner == 'VWFS'

  // If it's a special partner, render their specific logo instead of the app switcher
  if (isEssox) {
    return (
      <Link to='/'>
        <Image alt='Essox' h='52px' my={-2.5} src={`${import.meta.env.VITE_BASE_URL}/images/Essox.png`} objectFit='contain' />
      </Link>
    )
  }

  if (isTUkas) {
    return (
      <Link to='/'>
        <Image alt='TUkas' h='36px' my={-1} src={`${import.meta.env.VITE_BASE_URL}/images/tukas.svg`} />
      </Link>
    )
  }

  if (isVWFS) {
    return (
      <Link to='/leasing'>
        <HStack>
          <Image alt='Logo' h='36px' my={-1} src={`${import.meta.env.VITE_BASE_URL}/images/vwfs.svg`} />
          <Image alt='VWFS' h='36px' my={-1} src={`${import.meta.env.VITE_BASE_URL}/images/vwfs2.svg`} />
        </HStack>
      </Link>
    )
  }

  const handleCurrentProductClick = () => {
    onClose()

    if (lastApp?.path)
      navigate(lastApp.path)
  }

  const handleCaretClick = () => {
    if (isOpen)
      onClose()
    else onOpen()
  }

  const remainingApps = apps.filter((p) => p.name !== lastApp?.name && !!p.path && p.standalone)

  return (
    <Box ref={boxRef} sx={{ minW: '200px', position: 'relative' }}>
      {lastApp && (
        <AppLink
          name={lastApp.name}
          icon={lastApp.icon}
          iconSize={lastApp.iconSize}
          isCurrent
          hasCaret={remainingApps.length > 0}
          onCaretClick={handleCaretClick}
          onImageClick={handleCurrentProductClick}
        />
      )}

      {isOpen && (
        <Grid
          sx={{
            w: '100%',
            position: 'absolute',
            top: 'calc(100% + 1rem)',
            left: 0,
            bgColor: 'white',
            borderBottomRadius: 'md',
            boxShadow: 'xl',
            overflow: 'hidden',
            zIndex: 'popover',
          }}
        >
          {remainingApps.map((app, index) => {
            return (
              <Box
                key={index}
                onClick={() => {
                  handleCaretClick()
                  navigate(app.path!)
                }}
              >
                <AppLink
                  name={app.name}
                  icon={app.icon}
                  iconSize={app.iconSize}
                  sx={{ w: '100%' }}
                />

                <Divider sx={{ mx: 'auto', w: 'calc(100% - 1rem)' }} />
              </Box>
            )
          })}
        </Grid>
      )}
    </Box>
  )
}
